import type { ReactNode } from 'react';
import { translateCurrentShellMessage } from '@/features/language/useI18n';
import styles from './MainContent.module.css';

/**
 * MainContent component props
 */
export interface MainContentProps {
  /** Content to render */
  children: ReactNode;
  /** Use full width (no max-width constraint) */
  fullWidth?: boolean;
  /** Remove padding */
  noPadding?: boolean;
  /** Show loading overlay */
  isLoading?: boolean;
}

/**
 * Main content area component
 *
 * Features:
 * - Content area with optional constraints
 * - Scrollable container
 * - Loading state overlay
 * - Error boundary wrapper
 *
 * @example
 * ```tsx
 * <MainContent>
 *   <DashboardPage />
 * </MainContent>
 * ```
 */
export function MainContent({
  children,
  fullWidth = false,
  noPadding = false,
  isLoading = false,
}: MainContentProps) {
  const contentClasses = [
    styles.content,
    fullWidth ? styles.fullWidth : '',
    noPadding ? styles.noPadding : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <main className={styles.mainContent}>
      {isLoading && (
        <div className={styles.loadingOverlay}>
          {/* TODO: Replace with Spinner component */}
          <div className={styles.spinner}>{translateCurrentShellMessage('Loading...')}</div>
        </div>
      )}
      <div className={contentClasses}>{children}</div>
    </main>
  );
}

export default MainContent;
